import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const SEEN_KEY_PREFIX = 'waterlily.freemiumIntentRouterSeen.';

function seenKey(clientId: string) {
  return `${SEEN_KEY_PREFIX}${clientId}`;
}

function hasSeen(clientId: string): boolean {
  try {
    return localStorage.getItem(seenKey(clientId)) === '1';
  } catch {
    return false;
  }
}

function markSeen(clientId: string) {
  try {
    localStorage.setItem(seenKey(clientId), '1');
  } catch {
    // Private mode / storage disabled — fall back to showing it again next time.
  }
}

/**
 * Controls the "What are you trying to do next?" intent router for unlocked
 * freemium advisors. It opens automatically the first time a client's planning
 * area is visited and not again after that.
 *
 * It does NOT open when the advisor was routed here for a specific action — a
 * post-payment continue (?freemiumPaid) or a deep-link to a tool (?fxAction) —
 * so the router never covers the thing they were just sent to.
 */
export function useFreemiumIntentRouter(clientId: string, enabled: boolean) {
  const [searchParams] = useSearchParams();
  const suppressOnMount = useRef(
    searchParams.has('fxAction') || searchParams.has('freemiumPaid'),
  );
  const [showRouter, setShowRouter] = useState(false);
  const autoOpenChecked = useRef(false);

  useEffect(() => {
    if (!enabled || autoOpenChecked.current || !clientId || suppressOnMount.current) return;
    autoOpenChecked.current = true;
    if (!hasSeen(clientId)) setShowRouter(true);
  }, [enabled, clientId]);

  const closeRouter = useCallback(() => {
    markSeen(clientId);
    setShowRouter(false);
  }, [clientId]);

  return { showRouter: enabled && showRouter, closeRouter };
}
